import { auth } from '@/lib/firebase';

/** Cloud Function root — no trailing slash. Paths are appended (e.g. `/api/jobs`). */
export function getApiBaseUrl(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');
  if (raw) return raw;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim();
  if (!projectId) {
    throw new Error('VITE_FIREBASE_PROJECT_ID is required when VITE_API_BASE_URL is empty');
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/speedyApiNew`;
}

const BASE_URL = getApiBaseUrl();

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const fetchWithToken = async (forceRefresh: boolean) => {
    const token = await user.getIdToken(forceRefresh);
    return fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  let response = await fetchWithToken(false);
  if (response.status === 401) {
    response = await fetchWithToken(true);
  }

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    let hint = '';
    if (response.status === 401) {
      hint =
        ' Token still rejected after refresh. Sign in against the same Firebase project as VITE_FIREBASE_PROJECT_ID / the API URL (dev vs prod).';
    }
    throw new Error(`API ${options.method ?? 'GET'} ${path} failed (${response.status}): ${body}${hint}`);
  }

  return response.json() as Promise<T>;
}

/** Public endpoints (e.g. new-customer Chargebee hosted page) — no Firebase session required. */
async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`API ${options.method ?? 'GET'} ${path} failed (${response.status}): ${body}`);
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postPublic: <T>(path: string, body: unknown) =>
    publicRequest<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
